<template>
  <div>

    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="q-mr-md capitalize ctrl-button"
          icon="keyboard_backspace"
          color="primary capitalize"
          :label="$store.state.stringsStore.strings.action.toOverview"
          align="left"
          @click="$router.push(`/${module}`)"
        />
      </div>
      <div class="col-6">
        <q-btn
          class="q-ml-md capitalize ctrl-button right"
          icon-right="delete"
          color="negative capitalize"
          :label="$store.state.stringsStore.strings.action.delete"
          align="right"
          @click="deleteUsergroup"
        />
        <q-btn
          class="q-ml-md capitalize ctrl-button right"
          icon-right="save"
          color="primary capitalize"
          :label="$store.state.stringsStore.strings.action.save"
          align="right"
          :disable="dataInvalid"
          :class="{'disabled-btn': dataInvalid}"
          @click="editUsergroup"
        />
      </div>
    </div>

    <!-- Columns -->
    <div class="row gutter-sm q-mb-lg">

      <!-- Left column -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">

        <!-- User block -->
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title"
              v-text="$store.state.stringsStore.strings.module.usergroups.single">
            </p>
            <div class="card-block scroll">
              <!-- Field: name -->
              <q-field class="q-mb-md"
                :error-label="$store.state.stringsStore.strings.module.usergroups.details.error.name">
                <q-input
                  type="text"
                  v-model.trim="$v.usergroup.name.$model"
                  :float-label="$store.state.stringsStore.strings.name"
                  @input="$v.usergroup.name.$touch"
                  :error="$v.usergroup.name.$error ? true : false">
                </q-input>
              </q-field>
            </div>
          </div>
        </div>

        <!-- Websites block -->
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title"
              v-text="$store.state.stringsStore.strings.module.usergroups.details.rights.label"
            />
            <div class="card-block no-padding scroll" v-if="rightsLoaded">
              <template v-for="(module, i) in rights">
                <q-list :key="i" class="custom-q-list accordion">
                  <q-collapsible
                    :duration="50" :icon="mapIcon(module.icon)" :label="getString(module.label)">
                    <div class="row">
                      <div class="col-6 q-pa-md q-pb-md"
                        :key="i" v-for="(submodule, i) in module.modules">
                        <p class="text-weight-bold" v-text="getString(submodule.label)"></p>
                        <div :key="i" v-for="(right, i) in submodule.rights">
                          <q-checkbox :val="right.code" v-model="usergroup.aRights"
                            :label="getString(right.label)"
                          />
                        </div>
                      </div>
                    </div>
                  </q-collapsible>
                </q-list>
              </template>
            </div>
          </div>
        </div>

      </div>

      <!-- Right column -->
      <div class="col-md-6 col-sm-12 col-xs-12">

        <!-- Search users block -->
        <div class="row q-mb-lg">
          <div class="full-width">
            <p class="card-title">
              {{ $store.state.stringsStore.strings.module.users.multiple }} ({{numberOfUsers}})
            </p>
            <!-- Searchfield -->
            <div class="q-mb-md" style="flex-grow: 1">
              <q-input
                color="grey-5"
                v-model="searchTerm"
                @input="applyFilters"
                :placeholder="$store.state.stringsStore.strings
                .module.usergroups.details.members.list.filter.label"
              />
            </div>
            <!-- Data table -->
            <div class="q-table" id="myTable">
              <vuetable
                ref="vuetable"
                class="table-block q-mb-md"
                :class="{ 'is-loading': isLoading }"
                :table-height="tableHeight"
                :api-url="'user'"
                pagination-path
                :api-mode="true"
                :noDataTemplate="noDataString('users')"
                :css="css.table"
                :http-fetch="fetchData"
                :fields="columns"
                :row-class="selectable ? applyRowClass : null"
                @vuetable:loading="onLoading"
                @vuetable:load-success="onSuccess"
                @vuetable:load-error="onError"
                @vuetable:pagination-data="onPaginationData">
                <!-- Custom fields -->
                <!-- User slot -->
                <div slot="name"
                  class="user-slot"
                  slot-scope="props">
                  <!-- User avatar -->
                  <img v-if="!fallbacks.includes(props.rowData.id)" :src="userAvatar(props.rowData.avatar)" @error="fallbackImage(props.rowData.id)" class="table-gravatar">
                  <img v-else :src="`${base_url}default-avatar.jpeg`" class="table-gravatar">
                  <!-- User full name -->
                  <div class="user-name">
                    <div
                      v-html="highlight(fullname(props.rowData.name), filters.name)">
                      <span v-if="props.rowData.active" class="label text-white label-inactive">{{ $store.state.stringsStore.strings.inactive }}</span>
                    </div>
                  </div>
                </div>

                <div slot="member" slot-scope="props">
                  <q-toggle
                    v-model="usergroup.aMembers" :val="props.rowData.id" color="secondary"/>
                </div>
              </vuetable>
              <!-- Pagination -->
              <div class="row pagination-bottom" id="myPagination">
                <!-- Per page select -->
                <div class="col">
                  <div class="row">
                    <q-select
                      class="btn-compact select-small"
                      hide-underline
                      color="grey"
                      v-model="perPage"
                      :options="perPageOptions"
                    />
                  </div>
                </div>
                <div class="col right-margin">
                  <vuetable-pagination
                    :on-each-side="1"
                    :css="css.pagination"
                    @vuetable-pagination:change-page="onChangePage"
                    ref="pagination">
                  </vuetable-pagination>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import Vuetable from 'vuetable-2';
import VuetablePagination from 'vuetable-2/src/components/VuetablePagination.vue';
import { required } from 'vuelidate/lib/validators';

import _cloneDeep from 'lodash.clonedeep';

// Helpers
import getString from '@/helpers/GetStrings';

// Mixins
import tableMixin from '@/mixins/tables/tableMixin';
import pagination from '@/mixins/tables/pagination';
import searchHighlight from '@/mixins/tables/searchHighlight';
import fetchData from '@/mixins/tables/fetchData';
import common from '../common';

export default {
  name: 'UsergroupsDetail',
  created() {
    this.fetchUsergroup();
    this.fetchRights();

    this.oldUsergroup = {};
  },
  mixins: [
    tableMixin,
    pagination,
    searchHighlight,
    fetchData,
    common,
  ],
  components: {
    Vuetable,
    VuetablePagination,
  },
  data() {
    return {
      env_url: process.env.VUE_APP_API_URL,
      base_url: process.env.BASE_URL,
      fallbacks: [],
      tableHeight: 'calc(100vh - 360px)',
      columns: [
        {
          name: '__slot:name',
          title: getString('name'),
          label: 'name',
          sortField: 'name',
          titleClass: 'text-left sortable vcol-l',
          dataClass: 'text-left vcol-l',
        },
        {
          name: '__slot:member',
          title: getString('member'),
          label: 'member',
          sortField: 'member',
          titleClass: 'text-left sortable vcol-xs',
          dataClass: 'text-left vcol-xs',
        },
      ],
      selectable: false,
      checked: false,
      data: null,
      tableContext: 'users',
      tableEndpoint: 'user',
      isLoading: false,
      filters: {
        name: '',
      },
      rightsLoaded: false,
      customSort: {
        field: 'usergroup',
      },
      usergroupId: '',
      usergroup: {
        name: '',
        aRights: [],
        aMembers: [],
      },

      dataInvalid: true,

      userNumber: 0,

      icons: {
        dashboard: 'dashboard',
        usergroups: 'people',
        developer: 'code',
        library: 'library_books',
        blog: 'art_track',
        news: 'import_contacts',
        press: 'camera',
        master: 'select_all',
        error: 'error_outline',
        faq: 'question_answer',
        page: 'web',
        hub: 'mdi-hexagon-multiple',
        product: 'devices_other',
        branch: 'mdi-source-branch',
        reference: 'domain',
        navigation: 'mdi-file-tree',
        content: 'toc',
        service: 'mdi-lifebuoy',
      },
    };
  },
  // Validations by Vuelidate
  validations: {
    usergroup: {
      name: { required },
    },
  },
  computed: {
    numberOfUsers() {
      return this.usergroup.aMembers.length;
    },
  },
  methods: {
    userAvatar(path) {
      return path.includes('www.gravatar.com') ? path : `${process.env.VUE_APP_API_URL}${path}`;
    },

    fallbackImage(id) {
      this.fallbacks.push(id);
    },
    getString,
    // Override mixin
    onSuccess(response) {
      // Set loading to false
      this.isLoading = false;
      // Modify the response object to fit vuetable.
      response.data.data = response.data.users;

      // For selectable tables, map row ids to an array.
      if (this.selectable) {
        this.rowData = response.data.data.map(row => row.id);
      }
    },

    async fetchRights() {
      try {
        const response = await this.axios.get('usergroup/rights');
        if (response.data.rights) {
          this.rights = response.data.rights;
          this.rightsLoaded = true;
        }
      } catch (error) {
        console.warn(error);
      }
    },
    fetchUsergroup() {
      // Substract usergroupId
      this.usergroupId = (this.$route.params.id) ? this.$route.params.id : '';
      // Fetch data from usergroup
      this.axios.get(`usergroup/${this.usergroupId}`)
        .then((response) => {
        // In the user detail page, if a given id is not found, we will always get a 200 OK response....
        // In this case, the response data object should be usergroup, not usergroups
          if (!response.data.usergroup) {
          // Redirect to users overview
            this.$router.push('/usergroups');
            return;
          }

          // Set data
          this.usergroup = {
            name: response.data.usergroup.name,
            aRights: response.data.usergroup.rights,
            aMembers: response.data.usergroup.users,
          };
          // Set non reactive variable for old usergroup data
          this.oldUsergroup = _cloneDeep(this.usergroup);
        })
        .catch((error) => {
          console.warn(error.request);
          // In the usergroups detail page, if a given id is not found, we will always get a 200 OK response....
          // In this case, the response data object should be usergroup, not usergroups
          if (error.response.status === 404) {
            // Notify nothing found
            this.$q.notify({
              message: this.$store.state.stringsStore.strings.module.usergroups.details.error.nothingFound,
              type: 'warning',
              timeout: 2000,
              position: 'top-right',
            });
          } else {
            // Throw general error notification
            this.$q.notify({
              message: this.$store.state.stringsStore.strings.error.general,
              position: 'top-right',
              timeout: 1000,
              type: 'warning',
            });
          }

          // Redirect to usergroups overview page when nothing found
          this.$router.push('/usergroups');
        });
    },
    // Gather UsergroupParams from data
    getUsergroupParams() {
      // Define
      const rights = [];
      const users = [];
      // Loop over clicked rights
      this.usergroup.aRights.forEach((right) => {
        rights.push(`rights[]=${right}`);
      });
      // Loop over switched mebers
      this.usergroup.aMembers.forEach((member) => {
        users.push(`users[]=${member}`);
      });
      // Return as formData
      return [
        rights.length ? rights.join('&') : null,
        users.length ? users.join('&') : null,
      ].filter(val => val !== null).join('&');
    },
    async editUsergroup() {
      try {
        const response = await this.axios.put(`usergroup/${this.usergroupId}/`,
          `${this.getUsergroupParams()}&name=${encodeURIComponent(this.usergroup.name)}`);
        if (response.data.status === 200) {
          // Set non reactive variable for old usergroup data
          this.oldUsergroup = _cloneDeep(this.usergroup);
          this.dataInvalid = true;

          // Fetch new me and navigation since the users own rights may have been affected.
          this.$store.dispatch('getMe');
          this.$store.dispatch('getNavigation');

          // Throw a positive notification
          this.$q.notify({
            message: this.$store.state.stringsStore.strings.module.usergroups.details.saved,
            position: 'top-right',
            timeout: 1000,
            color: 'positive',
          });
        }
      } catch (error) {
        // Throw a positive notification
        this.$q.notify({
          message: this.$store.state.stringsStore.strings.error.general,
          position: 'top-right',
          timeout: 1000,
          color: 'negative',
        });
      }
    },
    deleteUsergroup() {
      // Dialog
      this.$q.dialog({
        title: '',
        message: this.$store.state.stringsStore.strings
          .module.usergroups.list.dialogs.delete.single,
        ok: this.$store.state.stringsStore.strings.dialog.confirm.ok,
        cancel: this.$store.state.stringsStore.strings.dialog.confirm.cancel,
      }).then(() => {
        // delete call
        this.axios.delete(`usergroup/${this.usergroupId}`)
          .then(() => {
            // Refresh data to see the result
            this.$refs.vuetable && this.$refs.vuetable.refresh();
            this.$q.notify({
              message: this.$store.state.stringsStore.strings.module.usergroups.list.deleted.single,
              position: 'top-right',
              timeout: 1000,
              color: 'positive',
            });

            setTimeout(() => {
              this.$router.push('/usergroups');
            }, 1000);
          })
          .catch((error) => {
            // Error
            console.warn(error);
          });
      }).catch(() => {
        console.warn('Not removed');
      });
    },
    mapIcon(icon) {
      return this.icons[icon] ? this.icons[icon] : this.icons.error;
    },
    fullname(name) {
      return `${[name.firstname, name.surname].filter(val => val !== null).join(' ')}`;
    },
    // Returns true if objects are equal
    compareObjects(obj1, obj2) {
      return JSON.stringify(obj1) === JSON.stringify(obj2);
    },
  },
  watch: {
    // Put a watch on the usergroup property
    usergroup: {
      handler(val) {
        if (val.name !== '' && !this.$v.usergroup.name.$invalid && val.aRights.length > 0 && val.aMembers.length > 0 && !this.compareObjects(this.oldUsergroup, this.usergroup)) {
          this.dataInvalid = false;
        } else {
          this.dataInvalid = true;
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables'

.table-gravatar
  display:inline-block
  height 2rem
  width 2rem
  margin 0 1rem 0 0
  -webkit-background-size cover
  border-radius 50%

.user-slot
  display inline-flex
  vertical-align middle

.user-name
  line-height 2rem
</style>
